import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro'
import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BudgetsFiltersBody } from 'src/components/BudgetsBoxWithFilters/BudgetsBoxWithFilters'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import SvgChevronRight from 'src/components/Svgs/SvgChevronRight'
import UserAvatarAndName from 'src/components/UserAvatarAndName/UserAvatarAndName'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { BudgetRequestListModel } from 'src/models/budgetRequest.models'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { connectedPush } from 'src/navigation/Lnk'
import { updateBudgetRequestUrl } from 'src/navigation/urls'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { InfiniteScrollProps } from 'src/types'
import styled, { css, CSSProp } from 'styled-components'

type BudgetRequestsDataGridProps = {
  budgetRequests: BudgetRequestListModel[]
  isApprover?: boolean
  infiniteScrollProps?: InfiniteScrollProps<BudgetsFiltersBody>
  filters?: BudgetsFiltersBody
  clientHeight?: number
}

interface BudgetRequestsDataGridRow extends GridValidRowModel {
  id: string
  budget: BudgetRequestListModel
  index: number
}

const StyledDiv = styled.div<{
  budget?: CreditCardListModel
  wrapText?: boolean
}>`
  display: flex;
  align-items: center;
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  overflow: hidden;
  ${({ wrapText }): CSSProp => {
    return wrapText
      ? css`
          white-space: nowrap;
        `
      : css``
  }}
  text-overflow: ellipsis;
  ${({ budget }): CSSProp => {
    return budget && budget?.isPending
      ? css`
          ${getFontCss(
            14,
            styleConstants.colors.gray_dark,
            styleConstants.fontFamilies.regular,
          )};
        `
      : css``
  }}
`

const BudgetRequestsDataGrid: React.FC<BudgetRequestsDataGridProps> = (
  props,
): React.ReactElement => {
  const users = useSelector(selectUserListModelsFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const { analytics } = useAnalyticsContext()

  const dispatch = useDispatch()

  const rows: BudgetRequestsDataGridRow[] = props.budgetRequests.map(
    (budgetRequests, i) => {
      return {
        id: budgetRequests?.id,
        budget: budgetRequests,
        index: i,
      }
    },
  )

  const columns: Array<GridColDef<BudgetRequestsDataGridRow> | undefined> = [
    props.isApprover
      ? {
          field: 'dateOfRequest',
          headerName: translateSingleString(locale, 'Date of Request'),
          flex: 1,
          minWidth: 250,
          sortable: false,
          renderCell: (params): React.ReactElement => {
            const budgetRequests = params.row.budget
            return (
              <StyledDiv
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                <span
                  css={css`
                    margin-left: 6px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    ${getFontCss(
                      14,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {budgetRequests.updatedAtTimezonedString}
                </span>
              </StyledDiv>
            )
          },
          renderHeader: (params): React.ReactElement => {
            return (
              <div
                css={css`
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {params.colDef.headerName}
              </div>
            )
          },
        }
      : undefined,
    {
      field: 'name',
      headerName: translateSingleString(locale, 'Name'),
      sortable: false,
      flex: 1,
      minWidth: 200,
      maxWidth: 400,
      renderCell: (params): React.ReactElement => {
        const budgetRequests = params.row.budget
        return (
          <StyledDiv
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <img
              css={css`
                height: 20px;
              `}
              src={budgetRequests.budgetTypeImageSrc}
              alt={''}
            />
            <span
              css={css`
                margin-left: 6px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              `}
            >
              {budgetRequests.displayName}
            </span>
          </StyledDiv>
        )
      },
      renderHeader: (params): React.ReactElement => {
        return (
          <div
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {params.colDef.headerName}
          </div>
        )
      },
    },
    {
      field: 'userApprover',
      headerName: translateSingleString(
        locale,
        props.isApprover ? 'User' : 'Approver',
      ),
      sortable: false,
      minWidth: 200,
      maxWidth: 400,
      renderCell: (params): React.ReactElement => {
        const budgetRequests = params.row.budget
        const user = props.isApprover
          ? budgetRequests.requester
          : budgetRequests.approver
        const displayUser = users[user.id] || user
        return (
          <StyledDiv
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <UserAvatarAndName user={displayUser} />
          </StyledDiv>
        )
      },
      renderHeader: (params): React.ReactElement => {
        return (
          <div
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {params.colDef.headerName}
          </div>
        )
      },
    },
    {
      field: 'type',
      headerName: translateSingleString(locale, 'Type'),
      sortable: false,
      minWidth: 200,
      maxWidth: 400,
      renderCell: (params): React.ReactElement => {
        const budgetRequests = params.row.budget
        return (
          <StyledDiv
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <span
              css={css`
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {budgetRequests.recurs ? (
                <Trns source={'Recurring'} />
              ) : (
                <Trns source={'Standard'} />
              )}
            </span>
          </StyledDiv>
        )
      },
      renderHeader: (params): React.ReactElement => {
        return (
          <div
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {params.colDef.headerName}
          </div>
        )
      },
    },
    props.isApprover
      ? {
          field: 'activeUntil',
          headerName: translateSingleString(locale, 'Active Until'),
          sortable: false,
          minWidth: 200,
          renderCell: (params): React.ReactElement => {
            const budgetRequests = params.row.budget
            return (
              <StyledDiv
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.highlight,
                    styleConstants.fontFamilies.regular,
                  )};
                `}
              >
                {budgetRequests.recurs ? (
                  <Trns source={'See Request Details'} />
                ) : (
                  <>{budgetRequests.untilTimezonedString}</>
                )}
              </StyledDiv>
            )
          },
          renderHeader: (params): React.ReactElement => {
            return (
              <div
                css={css`
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {params.colDef.headerName}
              </div>
            )
          },
        }
      : undefined,
    {
      field: 'limit',
      headerName: translateSingleString(locale, 'Limit'),
      sortable: false,
      minWidth: 200,
      maxWidth: 400,
      renderCell: (params): React.ReactElement => {
        const budgetRequests = params.row.budget
        return (
          <StyledDiv
            css={css`
              ${getFontCss(
                14,
                themeConstants.colors.highlight,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            {budgetRequests.issuedAmountLabel}
          </StyledDiv>
        )
      },
      renderHeader: (params): React.ReactElement => {
        return (
          <div
            css={css`
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {params.colDef.headerName}
          </div>
        )
      },
    },
    {
      field: 'chevron',
      headerName: translateSingleString(locale, 'Chevron'),
      disableColumnMenu: true,
      resizable: false,
      sortable: false,
      hideable: false,
      headerClassName: 'hideRightSeparator',
      width: 20,
      renderCell: (): React.ReactElement => {
        return (
          <StyledDiv>
            <SvgChevronRight
              css={css`
                transform: scale(0.75);
              `}
            />
          </StyledDiv>
        )
      },
      renderHeader: (): React.ReactElement => <></>,
    },
  ]

  const columnsSafe = useMemo(
    () =>
      columns.filter(
        (column): column is GridColDef<BudgetRequestsDataGridRow> =>
          column !== undefined,
      ),
    [columns],
  )

  return (
    <DataGrid
      height={props.clientHeight}
      rows={rows}
      columns={columnsSafe}
      filtersBody={props.filters || {}}
      infiniteScrollProps={props.infiniteScrollProps}
      rowHeight={DataGridRowHeight.Comfy}
      onColumnVisibilityModelChange={(): void => {
        analytics?.track('budget requests - column selector', {
          screen: calculatedBackgroundLocation.pathname.replace(
            /\/cc_[a-zA-Z0-9]+/,
            '',
          ),
        })
      }}
      alwaysVisibleColumns={['name', 'dateOfRequest']}
      disableColumnFilter
      onRowClick={(row): void => {
        const budgetRequests = row.row.budget
        dispatch(
          connectedPush({
            pathname: updateBudgetRequestUrl(budgetRequests?.id),
            search: '',
            hash: '',
            state: {},
          }),
        )
        // TODO do we need analytics in the pending budgets/needs approval tabs ?
        // analytics?.track(
        //   'budget - select budget - item press',
        //   {
        //     intent: 'select budget', budgetSelected: budget.id, interaction: 'item press',
        //   },
        // )
      }}
    />
  )
}

export default BudgetRequestsDataGrid
